import {
  BotIcon,
  CheckIcon,
  MoreHorizontalIcon,
  PencilIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import type { CmmdTaskComment } from "@cmmd-forge/contracts";

import { MarkdownContent } from "~/components/rich-content/MarkdownContent";
import { Avatar } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { Textarea } from "~/components/ui/textarea";
import { formatTaskDateTime } from "./taskDateTime";
import { ProjectTaskCommentDeleteDialog } from "./ProjectTaskCommentDeleteDialog";

export interface ProjectTaskCommentRowProps {
  readonly comment: CmmdTaskComment;
  readonly pending: boolean;
  readonly onUpdate: (commentKey: string, content: string) => Promise<void>;
  readonly onDelete: (commentKey: string) => Promise<void>;
}

export function ProjectTaskCommentRow(props: ProjectTaskCommentRowProps) {
  const [mode, setMode] = useState<"view" | "edit" | "delete">("view");
  const [draft, setDraft] = useState(props.comment.content);
  const author = commentAuthor(props.comment);
  const save = async () => {
    const content = draft.trim();
    if (!content || content === props.comment.content) {
      setDraft(props.comment.content);
      setMode("view");
      return;
    }
    try {
      await props.onUpdate(props.comment.commentKey, content);
      setMode("view");
    } catch {
      // Keep the editor and draft visible so the owner can retry.
    }
  };

  return (
    <li className="group/comment flex gap-3">
      {props.comment.actor.kind === "agent_member" ? (
        <span
          className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
          aria-label={`${props.comment.actor.agentKind} bot`}
        >
          <BotIcon className="size-4" aria-hidden />
        </span>
      ) : (
        <Avatar name={author.name} src={author.image} className="mt-0.5 size-8" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-h-8 items-start gap-2">
          <div className="min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:flex">
            <span className="text-sm font-medium text-foreground">{author.name}</span>
            {props.comment.actor.kind === "agent_member" ? (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary">
                {props.comment.actor.agentKind} bot · {props.comment.actor.agentId}
              </span>
            ) : null}
            <time className="text-xs text-muted-foreground" dateTime={props.comment.createdAt}>
              {formatTaskDateTime(props.comment.createdAt)}
            </time>
          </div>
          <CommentActions
            permissions={props.comment.permissions}
            disabled={props.pending}
            onEdit={() => {
              setDraft(props.comment.content);
              setMode("edit");
            }}
            onDelete={() => setMode("delete")}
          />
        </div>

        <CommentBody
          mode={mode}
          content={props.comment.content}
          draft={draft}
          pending={props.pending}
          onDraftChange={setDraft}
          onCancel={() => {
            setDraft(props.comment.content);
            setMode("view");
          }}
          onSave={() => void save()}
        />
        <CommentTrace comment={props.comment} />
      </div>
      <ProjectTaskCommentDeleteDialog
        open={mode === "delete"}
        pending={props.pending}
        onOpenChange={(open) => setMode(open ? "delete" : "view")}
        onConfirm={async () => {
          try {
            await props.onDelete(props.comment.commentKey);
            setMode("view");
          } catch {
            // Preserve the confirmation surface so the owner can retry.
          }
        }}
      />
    </li>
  );
}

function CommentTrace({ comment }: { readonly comment: CmmdTaskComment }) {
  const trace = [
    comment.sourceLane,
    comment.agentInstance ? `instance ${comment.agentInstance.instanceKey}` : null,
    comment.runId ? `run ${comment.runId}` : null,
    comment.threadId ? `thread ${comment.threadId}` : null,
    comment.attemptId ? `attempt ${comment.attemptId}` : null,
    comment.runtimeId ? `runtime ${comment.runtimeId}` : null,
  ].filter((value): value is string => value !== null);
  const initiatedBy =
    comment.initiator.kind === "human_user"
      ? `user ${comment.initiator.userId}`
      : comment.initiator.kind;
  const modifiedBy =
    comment.lastModifier && comment.updatedAt !== comment.createdAt
      ? ` · modified by ${comment.lastModifier.displayName}`
      : "";
  return (
    <p className="mt-1 break-all text-[11px] text-muted-foreground">
      Initiated by {initiatedBy} · {trace.join(" · ")}
      {modifiedBy}
    </p>
  );
}

function CommentActions(props: {
  readonly permissions: CmmdTaskComment["permissions"];
  readonly disabled: boolean;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}) {
  const canManage = props.permissions.canEdit || props.permissions.canDelete;
  if (!canManage) return null;

  return (
    <Menu>
      <MenuTrigger
        aria-label="Comment actions"
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring sm:size-7 sm:opacity-0 sm:group-hover/comment:opacity-100 sm:focus-visible:opacity-100"
        disabled={props.disabled}
      >
        <MoreHorizontalIcon className="size-4" aria-hidden />
      </MenuTrigger>
      <MenuPopup align="end">
        {props.permissions.canEdit ? (
          <MenuItem onClick={props.onEdit}>
            <PencilIcon aria-hidden />
            Edit comment
          </MenuItem>
        ) : null}
        {props.permissions.canDelete ? (
          <MenuItem variant="destructive" onClick={props.onDelete}>
            <Trash2Icon aria-hidden />
            Delete comment
          </MenuItem>
        ) : null}
      </MenuPopup>
    </Menu>
  );
}

function CommentBody(props: {
  readonly mode: "view" | "edit" | "delete";
  readonly content: string;
  readonly draft: string;
  readonly pending: boolean;
  readonly onDraftChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  if (props.mode !== "edit") {
    return (
      <MarkdownContent variant="compact" className="mt-1">
        {props.content}
      </MarkdownContent>
    );
  }

  return (
    <div className="space-y-2">
      <Textarea
        aria-label="Edit comment"
        value={props.draft}
        maxLength={10_000}
        disabled={props.pending}
        onChange={(event) => props.onDraftChange(event.currentTarget.value)}
        size="sm"
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={props.pending}
          onClick={props.onCancel}
        >
          <XIcon aria-hidden />
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={props.pending || !props.draft.trim()}
          onClick={props.onSave}
        >
          <CheckIcon aria-hidden />
          {props.pending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function commentAuthor(comment: CmmdTaskComment): {
  readonly name: string;
  readonly image: string | null;
} {
  if (comment.actor.kind === "agent_member") {
    return { name: comment.actor.displayName, image: null };
  }
  if (comment.user) {
    return {
      name: [comment.user.firstName, comment.user.lastName].filter(Boolean).join(" "),
      image: comment.user.profileImage,
    };
  }
  return {
    name: comment.actor.displayName,
    image: comment.actor.kind === "human_user" ? comment.actor.profileImage : null,
  };
}
